import { ReactNode, createContext, useContext, useState, useMemo, useCallback } from 'react';
import { TakerMarket } from '../data/useTakerMarkets';
import IncludeEligibleInvoicesModal from './IncludeEligibleInvoicesModal';

interface IncludeEligibleInvoicesContextType {
  closeModal: () => void;
  isOpen: boolean;
  openModal: (takerMarkets: TakerMarket[]) => void;
  takerMarkets: TakerMarket[] | null;
}

interface IncludeEligibleInvoicesProviderProps {
  children: ReactNode;
}

const IncludeEligibleInvoicesContext = createContext<IncludeEligibleInvoicesContextType | undefined>(undefined);

export const useIncludeEligibleInvoicesContext = () => {
  const context = useContext(IncludeEligibleInvoicesContext);

  if (!context) {
    throw new Error('useIncludeEligibleInvoicesContext must be used within a IncludeEligibleInvoicesProvider');
  }

  return context;
};

const IncludeEligibleInvoicesProvider = ({ children }: IncludeEligibleInvoicesProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [takerMarkets, setTakerMarkets] = useState<TakerMarket[] | null>(null);

  const openModal = useCallback((markets: TakerMarket[]) => {
    setTakerMarkets(markets);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // clear takerMarkets after modal is closed
    setTimeout(() => {
      setTakerMarkets(null);
    }, 300);
  }, []);

  const contextValue = useMemo(
    () => ({
      closeModal,
      isOpen,
      openModal,
      takerMarkets,
    }),
    [closeModal, isOpen, openModal, takerMarkets]
  );

  return (
    <IncludeEligibleInvoicesContext.Provider value={contextValue}>{children}</IncludeEligibleInvoicesContext.Provider>
  );
};

const IncludeEligibleInvoicesModalComponent = () => {
  const { closeModal, isOpen, takerMarkets } = useIncludeEligibleInvoicesContext();

  return takerMarkets ? (
    <IncludeEligibleInvoicesModal onClose={closeModal} open={isOpen} tab="buyerControls" takerMarkets={takerMarkets} />
  ) : null;
};

export const IncludeEligibleInvoicesWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <IncludeEligibleInvoicesProvider>
      <IncludeEligibleInvoicesModalComponent />
      {children}
    </IncludeEligibleInvoicesProvider>
  );
};
